import { Form, LoaderFunctionArgs, useLoaderData, useSearchParams } from "react-router-dom"

import { fetchAPI } from "../utils"
import { Header } from "../components"
import { SearchItem } from "../components/SearchItem"

export interface CrossQueueItem {
  workflowId: string
  step: string
  serviceName: string
  timeStamp: string
  stackTrace?: string
  pod?: string
  payload: Record<string, unknown>
}

interface SearchResultPage {
  items: CrossQueueItem[]
  totalCount: number
}

interface SearchResult {
  dlq: SearchResultPage
  processing: SearchResultPage
}

interface LoaderResponse {
  result: SearchResult | null
  query: string
  type: string
  page: number
  pageSize: number
}

async function loader({ request }: LoaderFunctionArgs): Promise<LoaderResponse> {
  const url = new URL(request.url)
  const query = url.searchParams.get("query") ?? ""
  const type = url.searchParams.get("type") ?? "all"
  const rawPage = Number.parseInt(url.searchParams.get("page") ?? "1", 10)
  const page = Math.max(1, Number.isNaN(rawPage) ? 1 : rawPage)
  const rawPageSize = Number.parseInt(url.searchParams.get("pageSize") ?? "10", 10)
  const pageSize = Math.min(100, Math.max(1, Number.isNaN(rawPageSize) ? 10 : rawPageSize))

  if (query === "") return { result: null, query, type, page, pageSize }

  const data = await fetchAPI<SearchResult>("/api/queues/search", {
    method: "POST",
    body: { query, type, page, pageSize },
  })

  return { result: data.result ?? null, query, type, page, pageSize }
}

function ResultSection({
  title,
  items,
  totalCount,
}: Readonly<{
  title: string
  items: CrossQueueItem[]
  totalCount: number
}>) {
  return (
    <section>
      <h2 className="mb-2 font-semibold text-base-content">
        {title}{" "}
        <span className="text-xs font-normal text-base-content/60">
          ({totalCount} result{totalCount !== 1 ? "s" : ""})
        </span>
      </h2>
      {totalCount === 0 ? (
        <p className="text-sm text-base-content/60">No results.</p>
      ) : (
        <div className="flex flex-col gap-2">
          {items.map((item) => (
            <SearchItem key={`${item.workflowId}-${item.step}-${item.timeStamp}`} searchItem={item} />
          ))}
        </div>
      )}
    </section>
  )
}

const Route = () => {
  const { result, query, type, page, pageSize } = useLoaderData() as LoaderResponse
  const [, setSearchParams] = useSearchParams()

  const totalDlq = result?.dlq.totalCount ?? 0
  const totalProcessing = result?.processing.totalCount ?? 0
  const totalPages = Math.max(1, Math.ceil(Math.max(totalDlq, totalProcessing) / pageSize))

  return (
    <div className="flex min-h-screen w-full flex-col items-start justify-start gap-5 overflow-y-auto overflow-x-hidden bg-base-300 px-5 py-5 scrollbar-none">
      <div className="w-full space-y-5">
        <Header />
        <Form className="flex flex-wrap items-center gap-3">
          <input
            type="text"
            name="query"
            placeholder="Enter search term"
            defaultValue={query}
            className="input input-bordered input-accent input-sm grow basis-64 bg-base-100 py-1 px-2 text-sm text-base-content focus:outline-none"
          />
          <select
            name="type"
            defaultValue={type}
            className="select select-bordered select-accent select-sm bg-base-100 text-base-content focus:outline-none"
          >
            <option value="all">All</option>
            <option value="dlq">DLQ only</option>
            <option value="processing">Processing only</option>
          </select>
          <select
            name="pageSize"
            defaultValue={String(pageSize)}
            className="select select-bordered select-accent select-sm bg-base-100 text-accent focus:outline-none"
          >
            {[10, 20, 50].map((s) => (
              <option key={s} value={s}>
                {s} per page
              </option>
            ))}
          </select>
          <input type="hidden" name="page" value="1" />
          <button type="submit" className="btn btn-outline btn-accent btn-sm text-sm">
            Search
          </button>
        </Form>
      </div>
      <div className="divider" />
      {result == null ? (
        <p className="text-sm text-base-content/60">Enter a search term above to begin.</p>
      ) : (
        <div className="flex w-full flex-col gap-6">
          <ResultSection title="Dead Letter Queue" items={result.dlq.items} totalCount={totalDlq} />
          <ResultSection title="Processing" items={result.processing.items} totalCount={totalProcessing} />
          {totalPages > 1 && (
            <div className="flex items-center gap-3 text-sm text-base-content">
              <span>
                Page {page} of {totalPages}
              </span>
              <button
                type="button"
                disabled={page === 1}
                onClick={() =>
                  setSearchParams((params) => {
                    params.set("page", String(page - 1))
                    return params
                  })
                }
                className="btn btn-outline btn-accent btn-sm disabled:bg-base-100 disabled:text-base-content"
              >
                Previous
              </button>
              <button
                type="button"
                disabled={page >= totalPages}
                onClick={() =>
                  setSearchParams((params) => {
                    params.set("page", String(page + 1))
                    return params
                  })
                }
                className="btn btn-outline btn-accent btn-sm disabled:bg-base-100 disabled:text-base-content"
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

Route.loader = loader

export default Route
